import { Badge, Box, Link, Text, VStack, chakra } from "@chakra-ui/react"
import type { SnowReportDay } from "../../../hooks/useResortSnowReports"

const columns = [
  "Local date / source",
  "Status",
  "Source last updated",
  "Measurements",
  "Last check",
]

const outcomeDisplay = {
  published: { label: "Published", color: "green" },
  waiting: { label: "No update detected", color: "orange" },
  failed: { label: "Failed", color: "red" },
}

type RowProps = {
  row: SnowReportDay
}

function localTimestamp(timestamp: string, timezone: string) {
  return new Date(timestamp).toLocaleString("en-CA", { timeZone: timezone })
}

function ReportSource({ row }: RowProps) {
  const { SourceURL, LastUpdatedMessage, Narrative } = row.Report
  const hasSourceLink = /^https?:\/\//.test(SourceURL)

  return (
    <>
      {hasSourceLink ? (
        <Link href={SourceURL} target="_blank" rel="noreferrer" color="blue.200">
          {LastUpdatedMessage || "Freshness message unavailable"}
        </Link>
      ) : "—"}
      {Narrative && <Text fontSize="xs">{Narrative}</Text>}
    </>
  )
}

function ReportMeasurements({ row }: RowProps) {
  const unconfirmedAt = row.Outcome !== "published" ? row.ObservedAt : null

  return (
    <>
      {(row.Report.Metrics ?? []).map((metric, index) => (
        <Text key={index} fontSize="sm">
          {metric.Label}{metric.Location ? ` (${metric.Location})` : ""}: {metric.Measurement} cm{" "}
          <Box as="span" color="gray.400">
            ({metric.OriginalMeasurement} {metric.OriginalUnit})
          </Box>
        </Text>
      ))}
      {unconfirmedAt && (
        <Text color="gray.400" fontSize="xs">
          Unconfirmed observation · {localTimestamp(unconfirmedAt, row.Timezone)}
        </Text>
      )}
    </>
  )
}

function ObservationRow({ row }: RowProps) {
  const status = outcomeDisplay[row.Outcome]

  return (
    <chakra.tr>
      <chakra.td>
        <Text>{row.Date}</Text>
        <Text color="gray.400">{row.Source}</Text>
      </chakra.td>
      <chakra.td>
        <Badge colorPalette={status.color}>{status.label}</Badge>
        {row.FailureReason && (
          <Text color="red.300" fontSize="xs">{row.FailureReason}</Text>
        )}
      </chakra.td>
      <chakra.td><ReportSource row={row} /></chakra.td>
      <chakra.td><ReportMeasurements row={row} /></chakra.td>
      <chakra.td>
        {localTimestamp(row.CheckedAt, row.Timezone)}
        <Text color="gray.400" fontSize="xs">{row.Timezone}</Text>
      </chakra.td>
    </chakra.tr>
  )
}

export default function SnowReportObservationsTable({ rows }: { rows: SnowReportDay[] }) {
  return (
    <VStack align="stretch" gap="3">
      <Text fontWeight="medium">Daily observations</Text>
      <Box overflowX="auto">
        <chakra.table
          width="100%"
          bg="gray.800"
          color="gray.100"
          fontSize="sm"
          css={{
            "& th, & td": {
              px: "3",
              py: "2",
              borderBottomWidth: "1px",
              borderColor: "gray.700",
              textAlign: "left",
            },
            "& th": { color: "gray.300", fontWeight: "semibold" },
          }}
        >
          <chakra.thead>
            <chakra.tr>
              {columns.map((label) => (
                <chakra.th key={label}>{label}</chakra.th>
              ))}
            </chakra.tr>
          </chakra.thead>
          <chakra.tbody>
            {[...rows].reverse().map((row) => (
              <ObservationRow key={`${row.Date}-${row.Source}`} row={row} />
            ))}
          </chakra.tbody>
        </chakra.table>
      </Box>
    </VStack>
  )
}
